import { inject } from 'vue';

import axios from 'axios';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { useAuthenticationStore } from 'src/stores/authentication';
import { CreateANewNoteCommand } from 'src/stores/note/commands/CreateANewNoteCommand';
import { ProcessNoteCommand } from 'src/stores/note/commands/ProcessNoteCommand';
import { NoteQuery } from 'src/stores/note/queries';

import { InstanceAuthenticationConfiguration } from 'src/models/authentication';
import { CommandProcessIdHandling } from 'src/models/command';
import { Note, NoteRunType } from 'src/models/note';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateIdWithRemovedIdoAndInstanceId,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';
import { releaseRecord, retainRecord } from 'src/util/records/recordLeases';

import { NoteCallback } from './callbacks';
import {
	CreateTheIdoLinkedNotePayload,
	CreateTheNotePayload,
	DeleteTheNoteByAggregateIdPayload,
	DeleteTheNotePayload,
	FetchNotePayload,
	NoteStoreState,
	ReleaseNotePayload,
	ResetPayload,
	RetainNotePayload,
	SaveTheNotePayload,
	SetTheNoteStatePayload,
	UnsubscribeFetchNotePayload,
	UpdateTheNoteContentPayload,
	UpdateTheNoteFixedMaturityDatePayload,
	UpdateTheNoteTitlePayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const createDefaultState = (): NoteStoreState => ({
	initialLoadedNotes: new Map<string, Note>(),
	newNoteAggregateId: '',
	notes: new Map<string, Note>(),
});

const resolveStateCommandPayload = (
	payload: SetTheNoteStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

const parseAggregateIdFromCreateLinkedNoteResult = (result: unknown): string => {
	const rawAggregateId = (
		result as {
			data?: {
				data?: {
					aggregateId?: string;
				};
			};
		}
	).data?.data?.aggregateId;

	if (typeof rawAggregateId !== 'string') {
		return '';
	}

	try {
		JSON.parse(rawAggregateId);
		return aggregateIdWithRemovedIdoAndInstanceId(rawAggregateId);
	} catch {
		return '';
	}
};

const resolveActiveAuthenticationConfiguration = (): InstanceAuthenticationConfiguration | null =>
	useAuthenticationStore().activeInstanceAuthenticationConfiguration;

export const useNoteStore = defineStore('note', {
	actions: {
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.notes, this.initialLoadedNotes],
				payload.aggregateIds,
				payload.status
			);
		},
		createTheIdoLinkedNote(payload: CreateTheIdoLinkedNotePayload): void {
			void payload.commandBus
				?.sendCommand(
					new CreateANewNoteCommand(payload.authenticatedUserId, {
						idoWindow: {
							idoWindowIdoInstances: [
								{
									aggregateId: JSON.stringify({
										entryId: 0,
										instanceId: payload.instanceId,
										moduleName: 'module_note',
										sourceContainerId: 0,
										targetContainerId: 0,
										userId: String(payload.authenticatedUserId),
									}),
									aggregateVersion: 0,
									idoWindowIdos: [
										{
											aggregateId: JSON.stringify({
												entryId: 0,
												idoId: payload.idoId,
												instanceId: payload.instanceId,
												moduleName: 'module_note',
												sourceContainerId: 0,
												targetContainerId: 0,
												userId: payload.authenticatedUserId,
											}),
											aggregateVersion: 0,
										},
									],
								},
							],
						},
					})
				)
				?.then((result) => {
					this.setNewNoteAggregateId(parseAggregateIdFromCreateLinkedNoteResult(result));
				});
		},
		createTheNote(payload: CreateTheNotePayload): void {
			void payload.commandBus
				?.sendCommand(
					new CreateANewNoteCommand(payload.authenticatedUserId, {
						idoWindow: { idoWindowIdoInstances: [] },
					})
				)
				?.then((result) => {
					const aggregateId = (
						result as {
							data?: {
								data?: {
									aggregateId?: string;
								};
							};
						}
					).data?.data?.aggregateId;

					this.setNewNoteAggregateId(aggregateId ?? '');
				});
		},
		async deleteTheNote(payload: DeleteTheNotePayload): Promise<void> {
			const noteFromCollection = this.notes.get(normalizeAggregateId(payload.aggregateId));

			if (noteFromCollection === undefined) {
				return;
			}

			const activeInstanceAuthenticationConfiguration = resolveActiveAuthenticationConfiguration();
			if (activeInstanceAuthenticationConfiguration === null) {
				return;
			}

			const note = cloneDeep(noteFromCollection);
			this.unsubscribeFetchNote({
				aggregateId: note.aggregateId,
				queryStore: payload.queryStore,
			});

			await axios.post(`${activeInstanceAuthenticationConfiguration.url}/task/task/destroy`, {
				aggregateId: note.aggregateId,
				saveType: note.repetitionConfig.type,
			});
		},
		async deleteTheNoteByAggregateId(payload: DeleteTheNoteByAggregateIdPayload): Promise<void> {
			const activeInstanceAuthenticationConfiguration = resolveActiveAuthenticationConfiguration();
			if (activeInstanceAuthenticationConfiguration === null) {
				return;
			}

			await axios.post(`${activeInstanceAuthenticationConfiguration.url}/task/task/destroy`, {
				aggregateId: payload.aggregateId,
				saveType: 'withoutRepetition',
			});
		},
		discardChanges(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const baseline = this.initialLoadedNotes.get(normalizedAggregateId);
			if (baseline === undefined) {
				return;
			}

			this.notes.set(normalizedAggregateId, cloneDeep<Note>(baseline));
		},
		fetchNote(payload: FetchNotePayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				return payload.queryStore.query<Note>({
					callback: new NoteCallback((note) => this.setNote(note)),
					createTime: new Date(),
					query: new NoteQuery(payload.aggregateId),
					responseType: 'json',
					runType: NoteRunType,
				});
			}

			this.setNote(null);
			return Promise.resolve();
		},
		initialLoadedNote(aggregateId: string): Note | undefined {
			return this.initialLoadedNotes.get(normalizeAggregateId(aggregateId));
		},
		markNoteAsSaved(aggregateId: string): void {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const noteFromCollection = this.notes.get(normalizedAggregateId);
			if (noteFromCollection !== undefined) {
				this.initialLoadedNotes.set(normalizedAggregateId, cloneDeep<Note>(noteFromCollection));
			}
		},
		note(aggregateId: string): Note | undefined {
			return this.notes.get(aggregateId);
		},
		release(payload: ReleaseNotePayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const wasTheLastLease = releaseRecord(`note:${normalizedAggregateId}`);
			if (!wasTheLastLease || payload.shouldKeepData) {
				return;
			}

			this.notes.delete(normalizedAggregateId);
			this.initialLoadedNotes.delete(normalizedAggregateId);
			this.unsubscribeFetchNote({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);

			this.notes.delete(normalizedAggregateId);
			this.initialLoadedNotes.delete(normalizedAggregateId);
			this.newNoteAggregateId = '';
		},
		retain(payload: RetainNotePayload): void {
			retainRecord(`note:${normalizeAggregateId(payload.aggregateId)}`);
		},
		async saveTheNote(payload: SaveTheNotePayload): Promise<boolean> {
			const noteFromCollection = this.notes.get(normalizeAggregateId(payload.aggregateId));
			if (noteFromCollection === undefined) {
				return false;
			}

			const response = await payload.commandBus?.sendCommand(new ProcessNoteCommand(noteFromCollection));

			return response !== null && response !== undefined;
		},
		setNewNoteAggregateId(payload: string): void {
			this.newNoteAggregateId = payload || '';
		},
		setNote(payload: Note | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const noteFromCollection = this.notes.get(normalizedAggregateId);
			const initialLoadedNoteFromCollection = this.initialLoadedNotes.get(normalizedAggregateId);

			if (noteFromCollection !== undefined && !AggregateComparator.isEqual(noteFromCollection, payload)) {
				return;
			}

			if (noteFromCollection !== undefined && initialLoadedNoteFromCollection !== undefined) {
				const mergedNote = AggregateMerger.merge(
					initialLoadedNoteFromCollection,
					noteFromCollection,
					payload
				) as Note;
				this.notes.set(normalizeAggregateId(mergedNote.aggregateId), cloneDeep(mergedNote));
			} else {
				this.notes.set(normalizedAggregateId, payload);
			}

			this.initialLoadedNotes.set(normalizedAggregateId, cloneDeep(payload));
		},
		setTheNoteStateToReady(payload: SetTheNoteStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheNoteStateToSeen(payload: SetTheNoteStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheNoteStateToUnseen(payload: SetTheNoteStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		unsubscribeFetchNote(payload: UnsubscribeFetchNotePayload): void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.unsubscribeQuery(new NoteQuery(payload.aggregateId));
			}

			this.setNote(null);
		},
		updateTheNoteContent(payload: UpdateTheNoteContentPayload): void {
			const noteFromCollection = this.notes.get(normalizeAggregateId(payload.aggregateId));

			if (noteFromCollection !== undefined) {
				noteFromCollection.content = payload.content;
				this.notes.set(normalizeAggregateId(noteFromCollection.aggregateId), noteFromCollection);
			}
		},
		updateTheNoteFixedMaturityDate(payload: UpdateTheNoteFixedMaturityDatePayload): void {
			const noteFromCollection = this.notes.get(normalizeAggregateId(payload.aggregateId));

			if (noteFromCollection !== undefined) {
				noteFromCollection.fixedMaturityDate = payload.fixedMaturityDate;
				this.notes.set(normalizeAggregateId(noteFromCollection.aggregateId), noteFromCollection);
			}
		},
		updateTheNoteTitle(payload: UpdateTheNoteTitlePayload): void {
			const noteFromCollection = this.notes.get(normalizeAggregateId(payload.aggregateId));

			if (noteFromCollection !== undefined) {
				noteFromCollection.title = payload.title.replace(/\n/g, ' ');
				this.notes.set(normalizeAggregateId(noteFromCollection.aggregateId), noteFromCollection);
			}
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
